import { assertNever } from '@kofno/piper';
import { Maybe, just, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { error } from '../../../../ErrorHandling';
import { FlashAlert, errorAlert } from '../../../../Notifications/Types';
import { ParticipantResource } from '../../../../ParticipantsStore/Types';
import { Link } from '../../../../Resource/Types';
import { TPlainTextKey } from '../../../../Translations';
import { GroupParticipantsResource } from '../../Group/GroupParticipantsStore/Types';
import {
  State,
  addNewParticipant,
  participantsAdded,
  ready,
  readyWithNewParticipant,
  readyWithRemovedParticipant,
  removeParticipant,
  submit,
} from './Types';
import { BulkGroupMembershipsResource } from '../../Participants/BulkActions/AddParticipantsToGroup/UserGroupMembershipsForm/Store/Types';
import { FilterOption } from '../../../LearningCollections/LearningCollection/ParticipantDynamicTypeaheadSelect/UseParticipantDynamicTypeaheadSelect/Types';

class AddParticipantStore {
  @observable
  state: State;

  constructor(groupParticipantsResource: GroupParticipantsResource) {
    makeObservable(this);
    this.state = ready(groupParticipantsResource);
  }

  @action
  ready = (groupParticipantsResource: GroupParticipantsResource) => {
    this.state = ready(groupParticipantsResource);
  };

  @action
  readyWithNewParticipant = (participantResource: ParticipantResource) => {
    switch (this.state.kind) {
      case 'new-participant':
        this.state = readyWithNewParticipant(this.state, participantResource);
        break;
      case 'ready':
      case 'remove-participant':
      case 'error':
      case 'submitting':
      case 'participants-added':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  addParticipant = (option: FilterOption) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = addNewParticipant(this.state, option.payload.value);
        break;
      case 'new-participant':
      case 'remove-participant':
      case 'submitting':
      case 'error':
      case 'participants-added':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  removeParticipant = (participant: ParticipantResource) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = removeParticipant(this.state, participant.payload.id.toString());
        break;
      case 'new-participant':
      case 'remove-participant':
      case 'submitting':
      case 'error':
      case 'participants-added':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  readyWithRemovedParticipant = () => {
    switch (this.state.kind) {
      case 'remove-participant':
        this.state = readyWithRemovedParticipant(this.state);
        break;
      case 'ready':
      case 'new-participant':
      case 'submitting':
      case 'error':
      case 'participants-added':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submit = () => {
    switch (this.state.kind) {
      case 'ready':
        this.state = submit(this.state);
        break;
      case 'remove-participant':
      case 'new-participant':
      case 'error':
      case 'submitting':
      case 'participants-added':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  error = (msg: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'ready':
      case 'new-participant':
      case 'remove-participant':
      case 'submitting':
      case 'participants-added':
        this.state = error(msg);
        break;
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  participantsAdded = (bulkGroupMembershipsResource: BulkGroupMembershipsResource) => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = participantsAdded(bulkGroupMembershipsResource);
        break;
      case 'ready':
      case 'new-participant':
      case 'remove-participant':
      case 'error':
      case 'participants-added':
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get notification(): Maybe<FlashAlert> {
    switch (this.state.kind) {
      case 'error':
        return just(this.state).map(errorAlert);
      case 'ready':
      case 'new-participant':
      case 'submitting':
      case 'remove-participant':
      case 'participants-added':
        return nothing();
    }
  }

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.state.kind) {
      case 'ready':
      case 'new-participant':
      case 'remove-participant':
      case 'submitting':
        return this.state.groupParticipantsResource.links;
      case 'error':
      case 'participants-added':
        return [];
    }
  }
}

export default AddParticipantStore;
